import Typography from "../interfaces/Typography";
import Theme from "../interfaces/Theme";
import Colors from "../interfaces/Colors";
import Framing from "../interfaces/Framing";

export class Base implements Theme {

    /**
     * The Base is according Branch Desktop designs which are shared between Jobstack and Swipejobs
     * https://swipejobs.atlassian.net/wiki/spaces/PROD/pages/622166134/Branch+Desktop+Design
     */

    protected colorPalette = {
        white: '#FFFFFF',
        black: '#000000',
        blackLight: '#33374C',
        blue: '#2574A9',
        bluePrimary: '#3675AA',
        blueLight: '#F0F8FF',
        blueHighlight: '#DFF2FF',
        blueDark: '#224870',
        blueDeep: '#13294B',
        green: '#509B02',
        greenPrimary: '#55D5AC',
        greenActive: '#48BD19',
        greenDark: '#11B93C',
        greenInsipid: '#8FF792',
        gray: '#D1D1D1',
        grayPrimary: '#808080',
        grayLight: '#F4F4F4',
        grayLightSecondary: '#E2E2E2',
        grayMediumDark: '#ACACAC',
        grayDark: '#6D7278',
        grayDarkSecondary: '#797776',
        grayDarker: '#191919',
        pink: '#FF4081',
        red: '#E82B2B',
        redActive: '#FF0000',
        redInsipid: '#FE6B6E',
        orange: '#DC8633',
        orangeDark: '#BA612D',
        yellow: '#FFD600',
        yellowInsipid: '#F6FC8C',
        /** @deprecated since version 0.2.*, use blueDark */
        blueDarkHover: '#14395D',
        /** @deprecated since version 0.2.*, use blueDark */
        blueGrayDark: '#2F344A'
    };

    protected translucentColorPalette = {
        borderShadowLight: 'rgba(51, 55, 76, 0.25)',
        borderShadowDark: `rgba(51, 55, 76, 0.31)`
    }

    get getColorsPalette () {
        return this.colorPalette;
    };

    colors: Colors = {
        general: {
            primaryColor: this.colorPalette.bluePrimary,
            secondaryColor: this.colorPalette.grayDark,
            tertiaryColor: this.colorPalette.bluePrimary,
            disabledColor: this.colorPalette.grayPrimary,
            blueLightColor: this.colorPalette.blueLight,
            blueDarkColor: this.colorPalette.blueDeep,
            greenDarkColor: this.colorPalette.greenDark,

            /** @deprecated since version 0.2.*, use general.disabledColor */
            primaryDisabledColor: this.colorPalette.grayPrimary,
            /** @deprecated since version 0.2.*, use general.disabledColor */
            secondaryDisabledColor: this.colorPalette.grayPrimary,
            /** @deprecated since version 0.2.*, use hover.primaryHoverColor */
            primaryHoverColor: this.colorPalette.blueDark,
            /** @deprecated since version 0.2.*, use hover.secondaryHoverColor */
            secondaryHoverColor: this.colorPalette.grayLight,
            /** @deprecated since version 0.2.*, use text.primaryTextColor */
            primaryTextColor: this.colorPalette.white,
            /** @deprecated since version 0.2.*, use text.secondaryTextColor */
            secondaryTextColor: this.colorPalette.black,
            /** @deprecated since version 0.2.*, use text.tertiaryTextColor */
            tertiaryTextColor: this.colorPalette.grayDarker,
            /** @deprecated since version 0.2.*, use general.blueLightColor */
            secondaryLightColor: this.colorPalette.blueLight,
            /** @deprecated since version 0.2.*, use border.secondaryBorder */
            tabbedDialogBorderBottom: this.colorPalette.blackLight,
            /** @deprecated since version 0.2.*, use text.errorTextMessage */
            errorTextMessage: this.colorPalette.redActive,
            /** @deprecated since version 0.2.*, use text.successTextMessage */
            successTextMessage: this.colorPalette.greenPrimary,
            /** @deprecated since version 0.2.*, use background.primaryBackgroundColor */
            primaryBackgroundColor: this.colorPalette.white,
            /** @deprecated since version 0.2.*, use background.secondaryBackgroundColor */
            secondaryBackgroundColor: this.colorPalette.grayLight,
            /** @deprecated since version 0.2.*, use background.tertiaryBackgroundColor */
            tertiaryBackgroundColor: this.colorPalette.grayMediumDark
        },
        hover: {
            primaryHoverColor: this.colorPalette.blueDark,
            secondaryHoverColor: this.colorPalette.grayLight,
            tertiaryHoverColor: this.colorPalette.grayLightSecondary
        },
        text: {
            primaryTextColor: this.colorPalette.white,
            secondaryTextColor: this.colorPalette.black,
            tertiaryTextColor: this.colorPalette.grayDarker,
            grayTextColor: this.colorPalette.grayDarkSecondary,
            passedTextColor: this.colorPalette.greenPrimary,
            tabbedDialogBorderBottom: this.colorPalette.blackLight,
            errorTextMessage: this.colorPalette.redActive,
            successTextMessage: this.colorPalette.greenPrimary,
            indeterminateTextMessage: this.colorPalette.orange
        },
        background: {
            primaryBackgroundColor: this.colorPalette.white,
            secondaryBackgroundColor: this.colorPalette.grayLight,
            tertiaryBackgroundColor: this.colorPalette.grayMediumDark,
            grayBackgroundColor: this.colorPalette.gray,
            grayLightBackgroundColor: this.colorPalette.grayLightSecondary,
            blueBackgroundColor: this.colorPalette.bluePrimary,
            successBackgroundColor: this.colorPalette.green,
            orangeBackgroundColor: this.colorPalette.orange,
            failBackgroundColor: this.colorPalette.red,
            blueHighlightBackgroundColor: this.colorPalette.blueHighlight,
            disabledBackgroundColor: this.colorPalette.grayLightSecondary
        },
        button: {
            primaryButtonText: this.colorPalette.white,
            secondaryButtonText: this.colorPalette.white,
            shadow: 'rgba(0, 0, 0, 0.26)',

            /** @deprecated since version 0.2.*, use button.primaryButtonText */
            primaryColor: this.colorPalette.white
        },
        modal: {
            headerBackground: this.colorPalette.gray,
            innerBorder: this.colorPalette.gray,
            bodyBackground: this.colorPalette.white,
            headerBorder: this.colorPalette.blueDeep
        },
        title: {
            defaultColor: this.colorPalette.blackLight
        },
        table: {
            primaryTableColor: this.colorPalette.white,
            headerBackground: this.colorPalette.grayLight,
            modalHeaderBackground: this.colorPalette.grayLightSecondary,
            oddRowsBackground: 'none',
            evenRowsBackground: this.colorPalette.grayLight
        },
        border: {
            primaryBorder: this.colorPalette.grayPrimary,
            secondaryBorder: this.colorPalette.grayDark,
            tertiaryBorder: this.colorPalette.grayMediumDark,
            darkerBorder: this.colorPalette.grayDarker,
            highlightedBorder: this.colorPalette.pink,
            grayLightBorder: this.colorPalette.grayLightSecondary
        },
        hyperLinkColor: this.colorPalette.blue,
        ratingStar: {
            ratedColor: this.colorPalette.yellow,
            emptyColor: this.colorPalette.grayPrimary
        },
        status: {
            todo: this.colorPalette.redInsipid,
            inProgress: this.colorPalette.yellowInsipid,
            closed: this.colorPalette.greenInsipid
        }
    };

    framing: Framing = {
        border: {
            minimal: '0',
            tiny: '1px solid',
            small: '2px solid',
            medium: '4px solid',
            mediumPlus: '6px solid'
        },
        padding: {
            action: '6px 15px'
        },
        radius: {
            small: '5px',
            large: '10px',
            extraLarge: '15px'
        },
        button: {
            border: '0'
        },
        table: {
            innerBorder: '1px solid'
        },
        ratingStar: {
            spacing: '2px',
            dimension: '25px'
        },
        boxShadow: {
            selectDropDown: `${this.translucentColorPalette.borderShadowLight} 0px 4px 8px, ${this.translucentColorPalette.borderShadowLight} 0px 0px 1px`
        },
        modal: {
            headerSpace: {
                padding: '15px 15px 0',
                margin: '0'
            },
            headerBorder: '5px solid',
            content: {
                padding: '15px'
            }
        }
    };

    typography: Typography = {
        font: {
            family: {
                heading: 'Roboto-Large, "Helvetica Neue", sans-serif',
                buttons: 'Roboto, Roboto-Medium',
                handwriting: 'Tangerine',
                table: 'Roboto-Regular,"Helvetica Neue",Roboto,sans-serif',
                logo: 'Ubuntu, sans-serif'
            },
            weight: {
                buttons: 'bold'
            },
            size: {
                tiny: '9px',
                small: '12px',
                medium: '13px',
                normal: '14px',
                normalPlus: '15px',
                large: '16px',
                larger: '18px',
                extraLarge: '20px',
                extraLargeMedium: '22px',
                extraLargePlus: '24px',
                major: '32px',
                extraMajor: '36px',
                extraMajorPlus: '40px'
            },
            title: {
                textTransform: 'none'
            }
        },
        icon: {
            size: {
                small: '35px',
                medium: '50px',
                normal: '70px',
                normalPlus: '100px',
                large: '150px',
                larger: '200px',
                extraLarge: '250px',
            }
        },
        button: {
            text: {
                textTransform: 'none'
            }
        }
    }
}

const baseTheme = new Base();
export default baseTheme;




// WEBPACK FOOTER //
// webpack://frontendThemeProvider/src/themes/Base.ts